import { useTranslations } from "next-intl";
import Cookies from "js-cookie";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { defaultLocale } from "@lib/i18n/config";
import { useLocale } from "next-intl";
import Dropdown from "react-bootstrap/Dropdown";
import { DropdownButton } from "react-bootstrap";
import classNames from "classnames";
import Image from "../Image";
import IconSvg from "../IconSvg";

function LanguageSelector({ className }) {
  const t = useTranslations("common");

  const router = useRouter();
  const currentLanguage = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams().toString();

  const localesAvailable = process.env.NEXT_PUBLIC_LOCALES_AVAILABLE.split(",");
  const localesToDisplayOnDropdownMenu = localesAvailable.filter(
    (locale) => locale !== currentLanguage,
  );

  function getLocaleDetails(locale) {
    let languageName, countryFlag;

    switch (locale) {
      case "fr":
        languageName = t("language_selector.french");
        countryFlag = "franceFlag";
        break;
      case "de":
        languageName = t("language_selector.german");
        countryFlag = "germanyFlag";
        break;
      case "nl":
        languageName = t("language_selector.dutch");
        countryFlag = "netherlandsFlag";
        break;
      case "en":
        languageName = t("language_selector.english");
        countryFlag = "unitedKingdomFlag";
        break;
      case "it":
        languageName = t("language_selector.italian");
        countryFlag = "italyFlag";
        break;
      case "es":
        languageName = t("language_selector.spanish");
        countryFlag = "spainFlag";
        break;
      case "pt":
        languageName = t("language_selector.portuguese");
        countryFlag = "portugalFlag";
        break;
      default:
        languageName = t("language_selector.french");
        countryFlag = "franceFlag";
    }

    return { languageName, countryFlag };
  }

  function setLanguage(newLanguage) {
    Cookies.set("NEXT_LOCALE", newLanguage, { expires: 3650 });

    let pathWithoutLocale = pathname;
    if (currentLanguage && pathname.startsWith(`/${currentLanguage}`)) {
      pathWithoutLocale = pathname.slice(`/${currentLanguage}`.length) || "/";
    }

    let newUrl = newLanguage === defaultLocale ? "" : `/${newLanguage}`;
    newUrl += pathWithoutLocale;
    if (searchParams) newUrl += `?${searchParams}`;

    router.push(newUrl);
  }

  function displayLanguage(locale, withChevron = false) {
    const { languageName, countryFlag } = getLocaleDetails(locale);

    return (
      <div className="d-flex align-items-center justify-content-start gap-1 text-dark">
        <Image
          src={countryFlag}
          alt={countryFlag}
          width={20}
          height={20}
        />
        <p className="mb-0 font-size-sm">{languageName}</p>
        {withChevron && (
          <span style={{ height: "16px", width: "16px", stroke: "currentColor" }}>
            <IconSvg name="chevron-down" />
          </span>
        )}
      </div>
    );
  }

  if (localesAvailable.length <= 1) return <div />;

  return (
    <section className="d-flex align-items-center justify-content-center gap-2">
      <p className="mb-0 font-size-sm">{t("language_selector.site_language")}</p>
      <div className={classNames("language-selector-container", className)}>
        <Dropdown drop="up-centered">
          <DropdownButton
            title={displayLanguage(currentLanguage, true)}
            variant="transparent"
            id="dropdown-basic"
          >
            {localesToDisplayOnDropdownMenu.map((locale) => (
              <Dropdown.Item
                key={locale}
                as="button"
                onClick={() => setLanguage(locale)}
              >
                {displayLanguage(locale)}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </Dropdown>
      </div>
    </section>
  );
}

export default LanguageSelector;
